import {
    ACard,
    ADataGrid,
    ADataGridColumn,
    ADataGridFilter,
    ASectionTitle,
    PagedSearchParams
} from "@atiautomacao/ati-ui-library";
import {Delete, Edit} from "@mui/icons-material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {Box, Button, CircularProgress, Tooltip, Typography, useTheme} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useSnackbar} from "notistack";
import {QueryClient, QueryClientProvider} from "react-query";
import {AUTHORITIES} from "../../../Config/Constants";
import {useAppSelector} from "../../../Config/Hooks";
import {hasPermission} from "../../../Shared/Auth/AuthenticationUtil";
import ConfirmDialog from "../../../Shared/Components/ConfirmDialog";
import {applyFiltersToColumns} from "../../../Utils/AlarmDateFilterValidationUtils";
import {extractFiltersFromColumns} from "../../../Utils/DataUitils";
import {toLowerCaseFilterValues} from "../../../Utils/FilterValueCaseUtils";
import {
    buildNumericFilterSanitizationMessage,
    NumericFilterDefinitions,
    sanitizeNumericFilters
} from "../../../Utils/NumericFilterValidationUtils";
import {RowTeleObjectData} from "../../../Shared/Types/TeleObject";
import {TeleObjectFormModal} from "./TeleObjectFormModal";
import {DeleteTeleObjectReactQuery, TeleObjectGridRow, TeleObjectReactQuery} from "./TeleObjectReactQuery";

const createColumn = (
    name: string,
    label: string,
    align: 'left' | 'right' | 'center',
    minWidth: number,
    filter?: ADataGridFilter
): ADataGridColumn => ({
    name,
    label,
    align,
    visibility: true,
    minWidth,
    breakpoints: {
        xs: true,
        sm: true,
        md: true,
        lg: true,
        xl: true
    },
    ...(filter && { filter })
});

const initColumnsTeleObjectGrid = [
    createColumn('teleObjectId', 'ID', 'center', 30, { name: 'teleObjectId', condition: 'equals', value: '', sort: 'none' }),
    createColumn('powerStationName', 'Usina', 'left', 100, { name: 'powerStationName', condition: 'contains', value: '', sort: 'none' }),
    createColumn('equipmentName', 'Equipamento', 'left', 100, { name: 'equipmentName', condition: 'contains', value: '', sort: 'none' }),
    createColumn('equipmentClassName', 'Classe de Equipamento', 'left', 100, { name: 'equipmentClassName', condition: 'contains', value: '', sort: 'none' }),
    createColumn('teleObjectConfigName', 'Nome do Teleobjeto', 'left', 100, { name: 'teleObjectConfigName', condition: 'contains', value: '', sort: 'none' }),
    createColumn('teleObjectConfigPoint', 'Ponto DNP3', 'left', 100, { name: 'teleObjectConfigPoint', condition: 'equals', value: '', sort: 'none' }),
    createColumn('teleObjectSpecificationTypeName', 'Tipo Específico', 'left', 100, { name: 'teleObjectSpecificationTypeName', condition: 'contains', value: '', sort: 'none' }),
    createColumn('teleObjectDataTypeName', 'Tipo de Dado', 'left', 100, { name: 'teleObjectDataTypeName', condition: 'contains', value: '', sort: 'none' }),
] as ADataGridColumn[];

/** Colunas numéricas: o input do filtro é saneado como nas telas de Alarmes. */
const NUMERIC_FILTER_DEFINITIONS: NumericFilterDefinitions = {
    teleObjectId: {label: 'ID'},
    teleObjectConfigPoint: {label: 'Ponto DNP3'},
};

/** RN07: limite de teleobjetos selecionáveis por operação de exclusão. */
const MAX_TELE_OBJECTS_PER_DELETION = 150;

/**
 * Tamanho de página padrão maior que o das demais telas de cadastro de propósito: o
 * critério de aceite da RN07 exige selecionar 150 teleobjetos em uma operação, e a
 * seleção da ADataGrid não atravessa páginas.
 */
const DEFAULT_PAGE_SIZE = MAX_TELE_OBJECTS_PER_DELETION;

/**
 * Texto da RN07. Só alarmes: no banco, apenas as FKs de ALARM e ALARMS_LAST_VALUE são
 * ON DELETE CASCADE — COMMAND_HISTORY e NOTIFICATION_GROUP_ITEM não caem por cascata.
 */
const deleteDescription = (
    <>
        {'Deseja apagar o(s) teleobjeto(s)? '}
        <strong>
            O histórico de alarmes associados a esse teleobjeto também serão apagados
        </strong>
    </>
);

export const TeleObjectSettingsPage = () =>{
    const theme = useTheme();
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSave = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.SAVE_SETTINGS, AUTHORITIES.SAVE_TELE_OBJECT
    ]);
    // Mesmo conjunto exigido pelo @RolesAllowed do DELETE /api/teleobject.
    const isAuthorizedToDelete = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.DELETE_SETTINGS, AUTHORITIES.DELETE_TELE_OBJECT
    ]);

    const [columns, setColumns] = useState<Array<ADataGridColumn>>(initColumnsTeleObjectGrid);
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(toLowerCaseFilterValues(extractFiltersFromColumns(initColumnsTeleObjectGrid)), 0, DEFAULT_PAGE_SIZE));
    const {data, isLoading} = TeleObjectReactQuery(pagedSearchParams);
    const totalOfRecords = data?.totalOfRecords ?? 0;

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [teleObjectBeingEdited, setTeleObjectBeingEdited] = useState<RowTeleObjectData | null>(null);

    const [idsToDelete, setIdsToDelete] = useState<Array<number>>([]);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
    const [clearSelection, setClearSelection] = useState(false);

    const {enqueueSnackbar} = useSnackbar();
    const mutationDelete = DeleteTeleObjectReactQuery();

    /** Enquanto a exclusão está em andamento o botão do lote vira "Processando". */
    const isDeleting = mutationDelete.isLoading;

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        const sanitizedResult = sanitizeNumericFilters(dataGridFilters, NUMERIC_FILTER_DEFINITIONS);

        // Só re-sincroniza as colunas quando o saneamento mudou algo; caso contrário a
        // ADataGrid dispara onFiltersChange de novo em laço.
        if (sanitizedResult.wasSanitized) {
            setColumns(applyFiltersToColumns(initColumnsTeleObjectGrid, sanitizedResult.filters));
            enqueueSnackbar(
                buildNumericFilterSanitizationMessage(sanitizedResult.sanitizedFieldLabels),
                {variant: "warning"}
            );
            return;
        }

        setPagedSearchParams(new PagedSearchParams(toLowerCaseFilterValues(sanitizedResult.filters), page, pageSize));
    }, [enqueueSnackbar]);

    // A grid é derivada da resposta da query: guardar as linhas em estado só duplicaria
    // o cache do React Query e abriria espaço para os dois ficarem fora de sincronia.
    const rows: Array<TeleObjectGridRow> = useMemo(() => {
        const actionsConfig = [
            {actionName: "edit", disabled: !isAuthorizedToSave},
            {actionName: "delete", disabled: !isAuthorizedToDelete},
        ];

        return (data?.data ?? []).map((item) => ({
            ...item,
            // A ADataGrid identifica cada linha por `row.data.id`. Sem esse campo todas as
            // linhas ficam com id indefinido e qualquer clique no checkbox casa com a primeira.
            id: item.teleObjectId,
            actionsConfig,
        }));
    }, [data?.data, isAuthorizedToSave, isAuthorizedToDelete]);

    // `onClearSelectedTempRows` da ADataGrid limpa a seleção na transição para true.
    // O efeito do filho roda antes do do pai, então baixar a flag aqui a transforma
    // num pulso de um ciclo e o próximo pedido de limpeza volta a ser percebido.
    useEffect(() => {
        if (clearSelection) {
            setClearSelection(false);
        }
    }, [clearSelection]);

    const handleAddNew = useCallback(() => {
        setTeleObjectBeingEdited(null);
        setIsFormOpen(true);
    }, []);

    const handleOnEdit = useCallback((row: RowTeleObjectData) => {
        setTeleObjectBeingEdited(row);
        setIsFormOpen(true);
    }, []);

    // Serve para fechar e para "salvou": a mutation já invalida a listagem,
    // então em ambos os casos só resta fechar o modal.
    const handleFormClose = useCallback(() => {
        setIsFormOpen(false);
        setTeleObjectBeingEdited(null);
    }, []);

    // RN07: a ADataGrid recusa a seleção acima de `maxRowSelected` e avisa por aqui.
    const handleOnMaxRowSelected = useCallback((isMax: boolean) => {
        if (isMax) {
            enqueueSnackbar(
                `É permitido excluir no máximo ${MAX_TELE_OBJECTS_PER_DELETION} teleobjeto por operação`,
                {variant: "warning", autoHideDuration: 2000}
            );
        }
    }, [enqueueSnackbar]);

    const handleOnRowSelect = useCallback((selectedRows: Array<RowTeleObjectData>) => {
        setSelectedIds(
            (selectedRows ?? [])
                .map(row => row.teleObjectId)
                .filter((id): id is number => id != null)
        );
    }, []);

    const handleOnDelete = useCallback((row: RowTeleObjectData) => {
        if (row?.teleObjectId == null) return;
        setIdsToDelete([row.teleObjectId]);
        setIsDeleteDialogOpen(true);
    }, []);

    const handleOnDeleteSelected = useCallback(() => {
        if (selectedIds.length === 0) return;
        setIdsToDelete(selectedIds);
        setIsDeleteDialogOpen(true);
    }, [selectedIds]);

    const handleDeleteDialogClose = useCallback(() => {
        setIsDeleteDialogOpen(false);
        setIdsToDelete([]);
    }, []);

    const handleDeleteDialogConfirm = useCallback(() => {
        if (idsToDelete.length === 0) return;
        mutationDelete.mutate(idsToDelete, {
            onSuccess: () => {
                setClearSelection(true);
                setSelectedIds([]);
            }
        });
        setIsDeleteDialogOpen(false);
        setIdsToDelete([]);
    }, [idsToDelete, mutationDelete]);

    return(
        <Box sx={{marginRight: 5, marginLeft: 3, marginTop: '0px'}}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{mb: 2}}
            >
                <ASectionTitle
                    styleContainer={{padding: '0'}}
                    styleSubtitle={{marginBottom: 0, paddingTop: 0}}
                    styleContainerTitle={{paddingBottom: 0}}
                    contentChildrenStyle={{marginBottom: 0, marginTop: -2}}
                    titleLabel={'Teleobjetos'}
                    subTitleLabel={'Cadastre e gerencie os teleobjetos dos equipamentos das usinas'}
                    contentChildren={
                        <Button
                            onClick={handleAddNew}
                            disabled={!isAuthorizedToSave}
                            variant="contained"
                            color="primary"
                            startIcon={<AddCircleOutlineIcon/>}
                            sx={{
                                paddingTop: 1,
                                paddingBottom: 1,
                                marginBottom: 1,
                                borderRadius: '8px',
                                boxShadow: '0px 2px 4px rgba(0,0,0,0.15)',
                                textTransform: 'uppercase',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                                letterSpacing: '0.02857em',
                                whiteSpace: 'nowrap',
                                height: 'fit-content'
                            }}
                        >
                            Novo Teleobjeto
                        </Button>
                    }
                />
            </Box>
            <ACard
                ariaLabel={"settings-tele-object-page"}
                styleProps={{
                    headerStyle: {height: 0, padding: 0},
                    contentStyle: {marginBottom: 0, padding: 0},
                    cardStyle: {
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                        borderBottomLeftRadius: '12px',
                        borderBottomRightRadius: '12px',
                        margin: 0,
                        border: 'none',
                        borderBottom: `1px solid ${theme.palette.divider}`
                    }
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 3,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        height: 64
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 500,
                            color: selectedIds.length > 0
                                ? theme.palette.text.primary
                                : theme.palette.text.secondary,
                            fontSize: "14px",
                        }}
                    >
                        {selectedIds.length} itens selecionados
                    </Typography>

                    <Box sx={{display: "flex", gap: 2}}>
                        <Button
                            variant="text"
                            disabled={!isAuthorizedToDelete || selectedIds.length === 0 || isDeleting}
                            onClick={handleOnDeleteSelected}
                            endIcon={isDeleting
                                ? <CircularProgress size={16} color="inherit" aria-label="Processando"/>
                                : <Delete/>}
                            sx={{
                                fontWeight: 600,
                                fontSize: "14px",
                                color: theme.palette.text.primary
                            }}
                        >
                            {isDeleting ? "Processando" : "EXCLUIR"}
                        </Button>
                    </Box>
                </Box>
                <ADataGrid
                    ariaLabel={"settings-grid-tele-object"}
                    tableContainerStyle={{borderRadius: 0}}
                    columns={columns}
                    rows={rows}
                    page={pagedSearchParams.page}
                    rowsPerPage={pagedSearchParams.size}
                    loading={isLoading}
                    totalOfRecords={totalOfRecords}
                    onFiltersChange={handleOnFiltersChange}
                    onRowSelect={handleOnRowSelect}
                    maxRowSelected={MAX_TELE_OBJECTS_PER_DELETION}
                    onMaxRowSelected={handleOnMaxRowSelected}
                    onClearSelectedTempRows={clearSelection}
                    disableActionsHeaderIcons={true}
                    /* A ADataGrid substitui o onClick de cada action e o chama com a
                       linha (row.data), não com o evento — daí o cast abaixo. */
                    actions={
                        <>
                            <IconButton name="edit" size={"small"} aria-label="Edit"
                                        disabled={!isAuthorizedToSave}
                                        onClick={(value) => handleOnEdit(value as unknown as RowTeleObjectData)}>
                                <Tooltip title="Editar">
                                    <Edit/>
                                </Tooltip>
                            </IconButton>
                            <IconButton name="delete" size={"small"} aria-label="Delete"
                                        disabled={!isAuthorizedToDelete}
                                        onClick={(value) => handleOnDelete(value as unknown as RowTeleObjectData)}>
                                <Tooltip title="Excluir">
                                    <Delete/>
                                </Tooltip>
                            </IconButton>
                        </>
                    }
                />
            </ACard>

            <TeleObjectFormModal
                open={isFormOpen}
                teleObject={teleObjectBeingEdited}
                onClose={handleFormClose}
                onSaved={handleFormClose}
            />

            <ConfirmDialog
                title={'Deletar'}
                description={deleteDescription}
                open={isDeleteDialogOpen}
                handleConfirm={handleDeleteDialogConfirm}
                handleClose={handleDeleteDialogClose}
            />
        </Box>
    );
}

export default function TeleObjectHomePage(){
    // O QueryClient precisa sobreviver aos re-renders vindos de ancestrais. Criando-o
    // no corpo do render, cada re-render instala um client novo: a listagem passa a
    // observar um cache e o invalidateQueries das mutations cai em outro, e a grid
    // fica desatualizada depois de salvar ou excluir.
    const [queryClient] = useState(() => new QueryClient());
    return (
        <QueryClientProvider client={queryClient}>
            <TeleObjectSettingsPage />
        </QueryClientProvider>
    )
}
